import { Calendar } from 'lucide-react';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { useSessionMinDate } from '@/hooks/useSessionMinDate';
import type {
  EventFormFieldChange,
  EventFormFieldValidation,
  EventFormState,
} from '@/types/event.types';

/** Props for {@link EventScheduleSection}. */
interface Props {
  form: EventFormState;
  onFieldChange: EventFormFieldChange;
  onBlur: (field: string) => void;
  validation: EventFormFieldValidation;
}

/** Date and time fields for the event. */
export function EventScheduleSection({ form, onFieldChange, onBlur, validation }: Readonly<Props>) {
  const { minSessionDate: minEventDate } = useSessionMinDate(form.establishmentId);

  function handleStartDateChange(value: string): void {
    onFieldChange('eventDate', value);
  }
  function handleStartDateBlur(): void {
    onBlur('eventDate');
  }
  function handleEndDateChange(value: string): void {
    onFieldChange('eventEndDate', value);
  }
  function handleEndDateBlur(): void {
    onBlur('eventEndDate');
  }
  function handleStartTimeChange(value: string): void {
    onFieldChange('startTime', value);
  }
  function handleEndTimeChange(value: string): void {
    onFieldChange('endTime', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Calendar className="w-4 h-4" />
        Date et horaires
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DatePicker
          label="Date de début"
          value={form.eventDate}
          onChange={handleStartDateChange}
          onBlur={handleStartDateBlur}
          minDate={minEventDate}
          maxDate={form.eventEndDate || undefined}
          disablePast={false}
          required
          state={validation.getFieldState('eventDate')}
          error={validation.getFieldError('eventDate')}
          inputSize="sm"
        />
        <DatePicker
          label="Date de fin (optionnelle)"
          value={form.eventEndDate || null}
          onChange={handleEndDateChange}
          onBlur={handleEndDateBlur}
          minDate={form.eventDate || minEventDate}
          disablePast={false}
          state={validation.getFieldState('eventEndDate')}
          error={validation.getFieldError('eventEndDate')}
          inputSize="sm"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <TimePicker
          label="Heure de début"
          value={form.startTime || ''}
          onChange={handleStartTimeChange}
          required
          error={validation.getFieldError('startTime')}
          inputSize="sm"
        />
        <TimePicker
          label="Heure de fin"
          value={form.endTime || ''}
          onChange={handleEndTimeChange}
          minTime={form.startTime || undefined}
          required
          error={validation.getFieldError('endTime')}
          inputSize="sm"
        />
      </div>
    </div>
  );
}
